// client/src/pages/Game/Battlefield/components/GameResult.jsx

import React from 'react';
import { challengeRematch } from '../../../../api/gameService';

/**
 * Écran de fin de partie — R34bb : refonte visuelle + récompenses.
 * @param {Object} result {winner, reason}
 * @param {Object} rewards {won, isAI, coins, elo, coinsBreakdown[], wagerPot} (peut être undefined)
 */
const GameResult = ({
  result,
  rewards,
  gameId,
  victoryImage,
  defeatImage,
  isAIGame,
  onRematch,
  onQuit
}) => {
  // R34cc : hooks AVANT tout retour conditionnel (règles des hooks)
  const [challengeState, setChallengeState] = React.useState(null); // null | 'sent' | 'refused' | 'error'
  const series = rewards && rewards.series;

  React.useEffect(() => {
    if (!series || !series.canRematch) return undefined;
    const onCancelled = () => setChallengeState((st) => (st === 'sent' ? 'refused' : st));
    window.addEventListener('elementum:match-cancelled', onCancelled);
    return () => window.removeEventListener('elementum:match-cancelled', onCancelled);
  }, [series]);

  if (!result) return null;

  const victory = result.winner === 'player';
  const entries = (rewards && Array.isArray(rewards.coinsBreakdown)) ? rewards.coinsBreakdown : [];
  const hasRewards = rewards && !rewards.isAI && (rewards.coins > 0 || rewards.elo !== 0 || entries.length > 0);

  const handleChallenge = async () => {
    if (!gameId) return;
    setChallengeState('sent');
    try {
      const r = await challengeRematch(gameId);
      if (!r || !r.success) setChallengeState('error');
    } catch (e) {
      setChallengeState('error');
    }
  };

  const challengeLabel =
    challengeState === 'sent' ? 'Défi envoyé… en attente' :
    challengeState === 'refused' ? 'Défi refusé — ⚔️ Redéfier' :
    challengeState === 'error' ? 'Erreur — ⚔️ Réessayer' :
    `⚔️ Défier ${series ? series.oppName : ''}`;

  return (
    <div className={`game-result-overlay${victory ? ' gr-victory' : ' gr-defeat'}`}>
      <div className="game-result-modal">
        {/* Fond visuel victoire / défaite */}
        <div
          className="gr-backdrop"
          style={{
            backgroundImage: `url(${victory ? victoryImage : defeatImage})`,
          }}
        />
        <div className="gr-vignette" />

        <div className="result-content">
          <div className={`gr-crest ${victory ? 'crest-win' : 'crest-lose'}`}>
            {victory ? '🏆' : '💀'}
          </div>

          <h2 className={`result-title ${victory ? 'title-win' : 'title-lose'}`}>
            {victory ? 'Victoire !' : 'Défaite'}
          </h2>
          <div className="result-badge">{result.reason}</div>

          {/* R34cc : score de série PvP */}
          {series && (
            <div className="gr-series">
              <span className="gr-series-label">Série contre {series.oppName}</span>
              <div className="gr-series-score">
                <span className="gr-series-me">Vous</span>
                <span className="gr-series-num">{series.myWins}</span>
                <span className="gr-series-dash">—</span>
                <span className="gr-series-num">{series.oppWins}</span>
                <span className="gr-series-opp">{series.oppName}</span>
              </div>
              <div className="gr-series-count">
                {series.gamesPlayed}/{series.maxGames || 3} duels joués
              </div>
            </div>
          )}

          {/* Récompenses */}
          <div className="gr-rewards">
            {!rewards && (
              <div className="gr-rewards-empty">Consulter le tableau de bord pour vos statistiques</div>
            )}
            {rewards && rewards.isAI && (
              <div className="gr-rewards-empty">
                Duel d'entraînement — aucune récompense (les pièces se gagnent en JcJ et via les quêtes)
              </div>
            )}
            {hasRewards && (
              <>
                <div className="gr-rewards-title">Récompenses</div>
                <div className="gr-rewards-list">
                  {rewards.coins > 0 && (
                    <div className="gr-reward-item coin">
                      <span className="gr-reward-icon">🪙</span>
                      <span className="gr-reward-value">+{rewards.coins}</span>
                      <span className="gr-reward-label">
                        {entries.length === 1 ? entries[0].label : 'pièces d\'or'}
                      </span>
                    </div>
                  )}
                  {rewards.elo !== 0 && (
                    <div className={`gr-reward-item elo ${rewards.elo > 0 ? 'elo-up' : 'elo-down'}`}>
                      <span className="gr-reward-icon">⚔️</span>
                      <span className="gr-reward-value">{rewards.elo > 0 ? '+' : ''}{rewards.elo}</span>
                      <span className="gr-reward-label">Elo</span>
                    </div>
                  )}
                  {rewards.wagerPot > 0 && (
                    <div className="gr-reward-item wager">
                      <span className="gr-reward-icon">🔥</span>
                      <span className="gr-reward-value">{rewards.wagerPot}</span>
                      <span className="gr-reward-label">mise en jeu</span>
                    </div>
                  )}
                </div>
                {entries.length > 1 && (
                  <div className="gr-rewards-detail">
                    {entries.map((e, i) => (
                      <div key={i} className="gr-rewards-line">
                        <span>{e.label}</span>
                        <span className="gr-line-coins">+{e.coins} 🪙</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="result-buttons">
            {isAIGame && (
              <button
                className="result-button rematch"
                onClick={onRematch}
              >
                <span className="button-text">Nouvelle partie</span>
                <span className="button-glow"></span>
              </button>
            )}
            {/* R34cc : défi de revanche PvP (2 défis max, 3 duels/série) */}
            {series && series.canRematch && (
              <button
                className="result-button rematch gr-challenge-btn"
                disabled={challengeState === 'sent'}
                onClick={handleChallenge}
              >
                <span className="button-text">
                  {challengeLabel}
                </span>
                <span className="button-glow"></span>
              </button>
            )}
            <button
              className="result-button quit"
              onClick={onQuit}
            >
              <span className="button-text">Quitter</span>
              <span className="button-glow"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameResult;
